import { useState } from 'react';
import { X } from 'lucide-react';
import { useCartStore } from '../lib/cart-store';
import { useTenantConfig } from '../lib/hooks';

const DISMISS_KEY = 'omni.storefront.dropshipBannerDismissed';

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(DISMISS_KEY);
  } catch {
    return null;
  }
}

/**
 * The attribution strip shown whenever a dropship ref is live — a share link
 * (per-product or store-wide) silently forces the WHOLE cart to retail (SRP)
 * pricing and routes checkout through the dropship order path, so an activated
 * member who clicked a friend's link would otherwise lose their member
 * discount, their PV and their comp without ever knowing why. This makes the
 * state visible and gives everyone a one-click way out.
 *
 * "Shop normally" clears the stored ref (the sharer earns nothing). The ✕ only
 * hides the strip for the session, keyed by ref so a NEW share link re-shows
 * it; attribution stays live.
 */
export function DropshipBanner() {
  const tenant = useTenantConfig();
  const dropshipRef = useCartStore((s) => s.dropshipRef);
  const setDropshipRef = useCartStore((s) => s.setDropshipRef);
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);

  const on = (tenant.data?.dropshippingIncome ?? false) && !!dropshipRef;
  if (!on || dismissed === dropshipRef) return null;

  return (
    <div className="border-b border-o/30 bg-o/10 text-[12.5px] text-[#555]">
      <div className="mx-auto flex min-h-[34px] max-w-[1180px] items-center gap-3 px-4 py-1.5">
        <span className="min-w-0 flex-1">
          You’re shopping through a member’s share link — prices show at retail, and your purchase
          credits them.
        </span>
        <button
          type="button"
          onClick={() => setDropshipRef(null)}
          className="flex-none rounded-full border border-o px-3 py-[3px] font-semibold text-o transition-colors hover:bg-o hover:text-white"
        >
          Shop normally
        </button>
        <button
          type="button"
          aria-label="Hide"
          onClick={() => {
            setDismissed(dropshipRef);
            try {
              sessionStorage.setItem(DISMISS_KEY, dropshipRef!);
            } catch {
              /* ignore */
            }
          }}
          className="flex-none text-[#999] hover:text-[#555]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
